import { CSSProperties, ReactNode, useState } from 'react';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';
import { Expand } from '.';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  className?: string;
  close?: boolean;
  expand?: boolean;
  icon?: IconProp;
  iconVisible?: boolean;
  onClose?: () => void;
  size?: 'header' | 'huge' | 'large' | 'medium' | 'small';
  style?: CSSProperties;
  text: string | ReactNode;
  title?: string;
  titleStyle?: CSSProperties;
  type?: string;
}

const Alert = ({
  className,
  close,
  expand,
  icon,
  iconVisible = true,
  onClose,
  size = 'medium',
  style,
  text,
  title,
  titleStyle,
  type = 'info'
}: Props) => {
  const { theme } = useReduxState();

  const [isExpanded, setIsExpanded] = useState(false);

  const typeBackgroundColors = {
    info: theme.lightGrey,
    'info-dark': theme.lightGreyDark,
    success: theme.successColor,
    'success-light': theme.successLightColor,
    warning: theme.warningColor,
    'warning-light': theme.warningLightColor,
    error: theme.dangerColor,
    'error-light': theme.dangerLightColor
  };

  const typeColors = {
    info: theme.textColorLighter,
    'info-dark': theme.textColorLight,
    success: 'white',
    'success-light': theme.successLightTextColor,
    warning: theme.warningTextColor,
    'warning-light': theme.warningLightTextColor,
    error: 'white',
    'error-light': theme.dangerLightTextColor
  };

  const typeIcons = {
    info: 'info-circle',
    'info-dark': 'info-circle',
    success: 'check-circle',
    'success-light': 'check-circle',
    warning: 'exclamation-triangle',
    'warning-light': 'exclamation-triangle',
    error: 'exclamation-circle',
    'error-light': 'exclamation-circle'
  };

  const sizeStyles = {
    small: {
      fontSize: '1.2rem',
      iconSize: '1.2rem',
      iconMargin: 10,
      iconTop: 0,
      padding: '7px 12px'
    },
    header: {
      fontSize: '1.3rem',
      iconSize: '1.3rem',
      iconMargin: 8,
      iconTop: 0,
      padding: '7px 12px'
    },
    medium: {
      fontSize: '1.3rem',
      iconSize: '1.3rem',
      iconMargin: 10,
      iconTop: 0,
      padding: '10px 14px'
    },
    large: {
      fontSize: '1.5rem',
      iconSize: '1.5rem',
      iconMargin: 10,
      iconTop: 0,
      padding: '12px 15px'
    },
    huge: {
      fontSize: '1.8rem',
      iconSize: '1.8rem',
      iconMargin: 10,
      iconTop: -1,
      padding: '14px 16px'
    }
  };

  const defaultIcon = typeIcons[type];
  const backgroundColor = typeBackgroundColors[type];
  const color = typeColors[type];
  const styles = sizeStyles[size];

  const handleExpand = () => setIsExpanded(!isExpanded);

  return (
    <div className={`alert ${className || ''}`} style={style}>
      <div className="inner">
        {iconVisible ? (
          <div
            className="icon"
            style={{
              marginRight: styles.iconMargin,
              position: 'relative',
              top: styles.iconTop
            }}
          >
            <Icon
              fixedWidth={true}
              icon={icon || defaultIcon}
              style={{ fontSize: styles.iconSize }}
            />
          </div>
        ) : null}

        {expand ? (
          <div className="header-expand" onClick={handleExpand}>
            <div className="title" style={titleStyle}>
              {title}
            </div>
            <div className="expand-icon">
              <Icon
                fixedWidth={true}
                icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                style={{ fontSize: styles.iconSize }}
              />
            </div>
          </div>
        ) : title ? (
          <div className="header">
            <div className="title" style={titleStyle}>
              {title}
            </div>
          </div>
        ) : (
          <div className="content">{text}</div>
        )}

        {close ? (
          <button className="close" onClick={onClose} type="button">
            <Icon
              fixedWidth={true}
              icon="times"
              style={{ fontSize: styles.iconSize }}
            />
          </button>
        ) : null}
      </div>

      {expand ? (
        <Expand isExpanded={isExpanded}>
          <div className="content-expand">{text}</div>
        </Expand>
      ) : title ? (
        <div className="content-with-title">{text}</div>
      ) : null}

      <style jsx>{`
        .alert {
          background-color: ${backgroundColor};
          border-radius: ${theme.buttonBorderRadius};
          color: ${color};
          font-size: ${styles.fontSize};
          font-weight: 600;
          line-height: 1.4;
          position: relative;
        }
        .inner {
          display: flex;
          padding: ${styles.padding};
        }
        .header {
          flex-grow: 1;
          min-width: 0;
        }
        .header-expand {
          cursor: pointer;
          display: flex;
          flex-grow: 1;
          min-width: 0;
        }
        .title {
          flex-grow: 1;
          font-weight: 700;
          margin: 0;
          min-width: 0;
        }
        .content {
          flex-grow: 1;
          min-width: 0;
        }
        .content-expand,
        .content-with-title {
          padding: ${styles.padding};
          padding-top: 0;
        }
        .content :global(p),
        .content-expand :global(p),
        .content-with-title :global(p) {
          margin: 10px 0 0;
        }
        .content :global(p:first-child),
        .content-expand :global(p:first-child),
        .content-with-title :global(p:first-child) {
          margin-top: 0;
        }
        .close {
          background-color: transparent;
          border: 0;
          color: inherit;
          cursor: pointer;
          margin: -10px -10px -10px ${styles.iconMargin}px;
          padding: 10px 0;
          text-align: center;
          width: 40px;
        }
      `}</style>
    </div>
  );
};

export default Alert;
